import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { Placement } from "@popperjs/core";
import { MouseEvent, useCallback, useRef, useId } from "react";
import { useDispatch, useSelector } from "react-redux";

import Suspense from "components/shared/Suspense";
import { BookButtonDrawer } from "features/BookButtonDrawer";
import { Drawer } from "features/Drawer";
import useIsLoggedIn from "hooks/user/useIsLoggedIn";
import { useUserBookStatus } from "store/bookButton/bookButtonSelector";
import { RootState } from "store/types";
import { uiActions } from "store/ui/uiSlice";

import StatusButton from "./StatusButton/StatusButton";

export type BookButtonSizeType = "sm" | "md";
export interface Props {
  bookId: number;
  referrerUserId?: number;
  size?: BookButtonSizeType;
  onClick?: any;
  placement?: Placement;
}

const closeKeyboard = async () => {
  if (Capacitor.isNativePlatform()) {
    await Keyboard.hide();
  } else {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }
};

export default function BookButton({
  bookId,
  referrerUserId,
  size = "md",
  onClick,
  placement = "bottom-start",
}: Props) {
  const bookButtonRef = useRef<HTMLDivElement>();
  const buttonId = useId();
  const isLoggedIn = useIsLoggedIn();
  const statusId = useUserBookStatus(bookId);
  const dispatch = useDispatch();
  const openButtonId = useSelector((state: RootState) => state.ui.openButtonId);
  const isOpen = openButtonId === buttonId;

  const openDropdown = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      if (e) {
        e.stopPropagation();
      }

      if (!isLoggedIn) {
        dispatch(uiActions.toggleLoginVisible("Login"));
        if (onClick) {
          onClick();
        }
        return;
      }

      await closeKeyboard();

      dispatch(uiActions.setOpenDropdown(isOpen ? null : buttonId));
    },
    [buttonId, isLoggedIn, dispatch, onClick, isOpen],
  );

  return (
    <div>
      <StatusButton
        bookId={bookId}
        el={bookButtonRef}
        statusId={statusId}
        size={size}
        referrerUserId={referrerUserId}
        onClick={openDropdown}
      />

      {isOpen && bookButtonRef.current ? (
        <Suspense
          fallback={
            <Drawer.Loading
              settings={{
                isOpen: true,
                onClose: () => {},
                ref: bookButtonRef.current,
                placement,
              }}
            />
          }
        >
          <BookButtonDrawer
            bookId={bookId}
            referrerUserId={referrerUserId}
            settings={{
              isOpen: true,
              onClose: () => dispatch(uiActions.setOpenDropdown(null)),
              ref: bookButtonRef.current,
              placement,
            }}
          />
        </Suspense>
      ) : (
        false
      )}
    </div>
  );
}
